/**
 * Confronto de localização no painel do eixo (E90.F2, GUS-14): o local do ato,
 * o endereço declarado do signatário e a estimativa de cada endereço de rede,
 * com município, distância e leitura — a mesma tabela dos documentos.
 *
 * A cautela fica FIXA sob a tabela, como a do cluster: quem lê "não cobre"
 * precisa ler, na mesma tela, que a estimativa tem precisão de cidade e que o
 * confronto não muda o grau.
 */
import {
  Badge,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ClassificacaoIpData } from '@/lib/exame_eletronico';
import {
  RAIO_COBERTURA_KM,
  type CoberturaDoEnderecoDeRede,
  type EnderecoDeclaradoAvaliadoData,
  type LocalizacaoApuradaData,
  type LocalizacaoDoEnderecoDeRedeData,
} from '@/lib/exame_eletronico_localizacao';

import { formatarDistancia } from './QuadroVerificacaoCluster';
import { Titulo } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.quadro;

const COBERTURA_VARIANT: Record<CoberturaDoEnderecoDeRede, 'success' | 'warning' | 'neutral'> = {
  cobre: 'success',
  nao_cobre: 'warning',
  nao_conclusivo: 'neutral',
  indeterminada: 'neutral',
  nao_se_aplica: 'neutral',
};

export function rotuloDaCobertura(cobertura: CoberturaDoEnderecoDeRede): string {
  return t.coberturaOpcoes[cobertura].replace('{raio}', String(RAIO_COBERTURA_KM));
}

function municipio(cidade?: string | null, estado?: string | null, pais?: string | null): string {
  const local = [cidade, estado].filter(Boolean).join('/');
  const estrangeiro = pais && !['brasil', 'brazil'].includes(pais.toLowerCase());
  if (estrangeiro) return local ? `${local} (${pais})` : pais;
  return local || t.municipioNaoIdentificado;
}

interface Linha {
  chave: string;
  referencia: string;
  municipio: string;
  distancia: string;
  leitura: JSX.Element | string;
}

function linhaDoEndereco(declarado: EnderecoDeclaradoAvaliadoData | null | undefined): Linha {
  if (!declarado) {
    return {
      chave: 'endereco',
      referencia: t.enderecoDeclarado,
      municipio: '—',
      distancia: '—',
      leitura: t.semEnderecoDeclarado,
    };
  }
  return {
    chave: 'endereco',
    referencia: `${t.enderecoDeclarado}: ${declarado.endereco}`,
    municipio: municipio(declarado.municipio, declarado.estado),
    distancia: formatarDistancia(declarado.distancia_m),
    leitura:
      declarado.distancia_m === null || declarado.distancia_m === undefined ? (
        (declarado.detalhe ?? '—')
      ) : (
        <span className="flex flex-col gap-sc-1">
          <Badge variant="neutral">{t.faixaOpcoes[declarado.faixa]}</Badge>
          <FonteDoEndereco declarado={declarado} />
        </span>
      ),
  };
}

/**
 * E168.F3.C01 (QA de 24/10): a coordenada geocodificada e a confiança do geocodificador ao lado da
 * faixa, como o exame imprime — a distância de 224 m só se reproduz com as duas coordenadas.
 */
function FonteDoEndereco({ declarado }: { declarado: EnderecoDeclaradoAvaliadoData }): JSX.Element {
  const partes: string[] = [];
  if (typeof declarado.latitude === 'number' && typeof declarado.longitude === 'number') {
    partes.push(
      t.coordenadaDoEndereco
        .replace('{lat}', declarado.latitude.toFixed(6))
        .replace('{lon}', declarado.longitude.toFixed(6)),
    );
  }
  if (typeof declarado.confianca_geocodificacao === 'number') {
    const confianca = declarado.confianca_geocodificacao.toFixed(2).replace('.', ',');
    partes.push(t.confiancaDaGeocodificacao.replace('{confianca}', confianca));
  }
  return (
    <span className="text-caption text-fg-dim" data-testid="quadro-confronto-fonte-do-endereco">
      {partes.join(' · ')}
    </span>
  );
}

/** E168.F3.C01: o papel na linha da rede pelo que o exame apura do endereço, não pelo declarado. */
function papelNaLinha(
  rede: LocalizacaoDoEnderecoDeRedeData,
  ips: readonly ClassificacaoIpData[],
): string {
  const ip = ips.find((i) => i.endereco === rede.endereco && i.papel === rede.papel);
  if (ip?.rede_de_entrega) return t.papelRedeDeEntrega;
  if (ip?.rede_da_instituicao) return t.papelRedeDaInstituicao;
  return t.papelRede[rede.papel];
}

function linhaDaRede(
  rede: LocalizacaoDoEnderecoDeRedeData,
  ips: readonly ClassificacaoIpData[],
): Linha {
  const conclusiva = rede.cobertura === 'cobre' || rede.cobertura === 'nao_cobre';
  return {
    chave: `rede-${rede.endereco}-${rede.papel}`,
    referencia: t.enderecoDeRede
      .replace('{endereco}', rede.endereco)
      .replace('{papel}', papelNaLinha(rede, ips)),
    municipio: municipio(rede.cidade, rede.estado, rede.pais),
    distancia: formatarDistancia(rede.distancia_m),
    leitura: (
      <span className="flex flex-col gap-sc-1">
        <Badge variant={COBERTURA_VARIANT[rede.cobertura]}>
          {rotuloDaCobertura(rede.cobertura)}
        </Badge>
        <span className="text-caption text-fg-dim">
          {conclusiva || rede.cobertura === 'nao_conclusivo'
            ? t.precisaoRedeOpcoes[rede.precisao]
            : (rede.detalhe ?? '')}
        </span>
      </span>
    ),
  };
}

export function ConfrontoDeLocalizacao({
  localizacao,
  ips = [],
}: {
  localizacao: LocalizacaoApuradaData | null | undefined;
  /** E168.F3.C01: as classificações do resultado, para o papel apurado de cada endereço. */
  ips?: readonly ClassificacaoIpData[];
}): JSX.Element {
  if (!localizacao) {
    return (
      <section data-testid="quadro-confronto">
        <Titulo>{t.confronto}</Titulo>
        <p className="text-body-sm text-fg-dim" data-testid="quadro-confronto-nao-apurado">
          {t.confrontoNaoApurado}
        </p>
      </section>
    );
  }
  const ato = localizacao.local_do_ato;
  const linhas: Linha[] = [
    {
      chave: 'ato',
      referencia: t.localDoAto,
      municipio: municipio(ato.municipio, ato.estado),
      distancia: '—',
      leitura: ato.situacao === 'confere' ? t.localDoAtoConfere : (ato.detalhe ?? '—'),
    },
    linhaDoEndereco(localizacao.endereco_declarado),
    ...localizacao.enderecos_de_rede.map((rede) => linhaDaRede(rede, ips)),
  ];
  return (
    <section data-testid="quadro-confronto">
      <Titulo>{t.confronto}</Titulo>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.colunaReferencia}</TableHead>
              <TableHead>{t.colunaMunicipio}</TableHead>
              <TableHead>{t.colunaDistanciaAoAto}</TableHead>
              <TableHead>{t.colunaLeitura}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {linhas.map((linha) => (
              <TableRow key={linha.chave} data-testid={`quadro-confronto-${linha.chave}`}>
                <TableCell className="max-w-[20rem]">{linha.referencia}</TableCell>
                <TableCell className="whitespace-nowrap">{linha.municipio}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <MonoData>{linha.distancia}</MonoData>
                </TableCell>
                <TableCell>{linha.leitura}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="mt-sc-2 text-caption text-fg-dim" data-testid="quadro-confronto-cautela">
        {t.confrontoCautela}
      </p>
    </section>
  );
}
